/**
 * GeoMapView — Phase 3.23.
 *
 * Reusable Nigeria choropleth using react-leaflet + OpenStreetMap tiles.
 * Drills Country → State → LGA. Pick a numeric metric from a dropdown;
 * polygons are tinted by value (emerald gradient). Click a state to
 * drill into LGAs; "Back to states" returns to the country view.
 *
 * Used in two surfaces:
 *  - `/map` page (full-width)
 *  - <GeoMapWidget /> on the dashboard widget grid (compact)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import { ArrowLeft, MapPin, Loader2 } from "lucide-react";
import api from "../lib/api";
import "leaflet/dist/leaflet.css";

const NIGERIA_CENTER = [9.082, 8.6753];
const NIGERIA_ZOOM = 6;

function intensity(value, max) {
  if (!value || max <= 0) return 0;
  return Math.min(0.18 + (value / max) * 0.72, 0.95);
}

function FitToBounds({ geojson }) {
  const map = useMap();
  useEffect(() => {
    if (!geojson || !geojson.features?.length) return;
    // build bounds from all coords; use L from window since useMap returns leaflet map
    const allCoords = [];
    const collect = (g) => {
      if (g.type === "Polygon") g.coordinates.forEach((ring) => ring.forEach((p) => allCoords.push([p[1], p[0]])));
      if (g.type === "MultiPolygon") g.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach((p) => allCoords.push([p[1], p[0]]))));
    };
    geojson.features.forEach((f) => collect(f.geometry));
    if (allCoords.length) {
      map.fitBounds(allCoords, { padding: [12, 12], animate: true, maxZoom: 8 });
    }
  }, [geojson, map]);
  return null;
}

export default function GeoMapView({ programIds, compact = false }) {
  const [metrics, setMetrics] = useState([]);
  const [metric, setMetric] = useState("");          // "" = sum across numerics
  const [level, setLevel] = useState("state");
  const [stateFilter, setStateFilter] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const layerRef = useRef(null);

  useEffect(() => {
    const q = programIds?.length ? `?program_ids=${programIds.join(",")}` : "";
    api.get(`/dashboard/v2/geo-metrics${q}`).then(({ data }) => setMetrics(data.metrics || [])).catch(() => {});
  }, [programIds]);

  useEffect(() => {
    setLoading(true);
    api.post("/dashboard/v2/geo-aggregate", {
      program_ids: programIds?.length ? programIds : null,
      indicator_id: metric || null,
      level,
      state_filter: level === "lga" ? stateFilter : null,
    })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [programIds, metric, level, stateFilter]);

  const valueByName = useMemo(() => {
    const m = {};
    (data?.features || []).forEach((f) => { m[f.name] = f; });
    return m;
  }, [data]);

  const styleFn = (feature) => {
    const rec = valueByName[feature.properties.name];
    const a = intensity(rec?.value || 0, data?.max_value || 0);
    return {
      fillColor: a ? "#10b981" : "#cbd5e1",
      fillOpacity: a || 0.1,
      color: "#0f172a",
      weight: 0.6,
      opacity: 0.7,
    };
  };

  const onEachFeature = (feature, layer) => {
    const name = feature.properties.name;
    const rec = valueByName[name] || { value: 0, row_count: 0 };
    const metricLabel = metric ? (metrics.find((m) => m.indicator_id === metric)?.name || "value") : "Total beneficiaries";
    layer.bindTooltip(
      `<b>${name}</b><br/>${metricLabel}: <b>${rec.value.toLocaleString()}</b><br/>${rec.row_count} rows`,
      { sticky: true, direction: "top", offset: [0, -2], className: "ae-map-tooltip" }
    );
    if (level === "state") {
      layer.on("click", () => { setStateFilter(name); setLevel("lga"); });
    }
    layer.on("mouseover", () => layer.setStyle({ weight: 1.6, color: "#10b981" }));
    layer.on("mouseout", () => {
      if (layerRef.current) layerRef.current.resetStyle(layer);
    });
  };

  // Force re-render of GeoJSON layer when level or data changes
  const layerKey = `${level}-${stateFilter || "all"}-${metric || "all"}-${data?.features?.length || 0}`;

  const totalLabel = data ? `Total ${(data.total || 0).toLocaleString()}` : "—";

  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden" data-testid="geo-map-view">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {level === "lga" && (
            <button
              onClick={() => { setLevel("state"); setStateFilter(null); }}
              className="flex items-center gap-1 text-xs text-slate-600 hover:text-emerald-700 font-medium"
              data-testid="geo-map-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> States
            </button>
          )}
          <p className="label-overline flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
            {level === "state" ? "Nigeria · States" : `Nigeria · ${stateFilter} LGAs`}
          </p>
          {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 font-mono">{totalLabel}</span>
          <select
            className="text-xs border border-slate-300 rounded-sm px-2 py-1 bg-white"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            data-testid="geo-map-metric-select"
          >
            <option value="">Total beneficiaries</option>
            {metrics.map((m) => (
              <option key={m.indicator_id} value={m.indicator_id}>{m.code} · {m.name}</option>
            ))}
          </select>
        </div>
      </div>
      <div className={compact ? "h-[380px]" : "h-[600px]"} data-testid="geo-map-canvas">
        <MapContainer
          center={NIGERIA_CENTER}
          zoom={compact ? 5 : NIGERIA_ZOOM}
          scrollWheelZoom={!compact}
          className="h-full w-full"
          style={{ background: "#f1f5f9" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {data?.geojson && (
            <GeoJSON
              key={layerKey}
              data={data.geojson}
              style={styleFn}
              onEachFeature={onEachFeature}
              ref={(el) => { layerRef.current = el; }}
            />
          )}
          {data?.geojson && <FitToBounds geojson={data.geojson} />}
        </MapContainer>
      </div>
      <div className="px-4 py-2 border-t border-slate-100 flex items-center justify-between flex-wrap gap-1.5 text-[10px] text-slate-500">
        <span>Click a state to drill into its LGAs. Tooltips show value + rows.</span>
        <div className="flex items-center gap-1">
          <span>Low</span>
          {[0.18, 0.36, 0.54, 0.72, 0.9].map((a) => (
            <span key={a} className="w-3 h-3 inline-block border border-slate-200" style={{ background: `rgba(16,185,129,${a})` }} />
          ))}
          <span>High</span>
        </div>
      </div>
    </div>
  );
}
